import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import agentApiService from '../../services/agentApiService';
import './TokenRecharge.css';

const TokenRecharge = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agentDetails, setAgentDetails] = useState(null);

  useEffect(() => {
    fetchTokenDetails();
  }, []);

  const fetchTokenDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🪙 Fetching agent token details...');
      const response = await agentApiService.getTokenRechargeDetails();
      
      if (response.success) {
        setAgentDetails(response.agentDetails);
      } else {
        throw new Error(response.message || 'Failed to fetch token details');
      }
    } catch (err) {
      console.error('Error fetching token details:', err);
      setError(err.message || 'Failed to load token details');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestTokens = () => {
    // Navigate to support ticket with state pre-selecting EXTRA_TOKEN_REQUEST
    navigate('/agent/dashboard/supportticket', {
      state: { defaultQueryType: 'EXTRA_TOKEN_REQUEST' }
    });
  };

  if (loading) {
    return (
      <div className="token-recharge-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading token account details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="token-recharge-container">
        <div className="error-message">
          <h3>Error Loading Token Account</h3>
          <p>{error}</p>
          <button onClick={fetchTokenDetails} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="token-recharge-container">
      {/* Header */}
      <div className="page-header">
        <h1>Token Account Overview</h1>
        <p>Manage your account tokens, view balance, and request extra tokens</p>
      </div>

      {/* Main Agent Details Card */}
      {agentDetails && (
        <div className="agent-details-card bg-white p-6 rounded-xl shadow-md border border-gray-200 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="stat-box bg-blue-50 p-4 rounded-lg border border-blue-100 flex items-center space-x-3">
              <div className="p-3 bg-blue-600 text-white rounded-full">
                <i className="fas fa-coins text-xl"></i>
              </div>
              <div>
                <div className="text-xs uppercase font-bold text-blue-600 tracking-wider">Available Tokens</div>
                <div className="text-2xl font-extrabold text-blue-900 mt-0.5">{agentDetails.tokens ?? 0}</div>
              </div>
            </div>

            <div className="stat-box bg-orange-50 p-4 rounded-lg border border-orange-100 flex items-center space-x-3">
              <div className="p-3 bg-orange-600 text-white rounded-full">
                <i className="fas fa-fire text-xl"></i>
              </div>
              <div>
                <div className="text-xs uppercase font-bold text-orange-600 tracking-wider">Total Tokens Used</div>
                <div className="text-2xl font-extrabold text-orange-900 mt-0.5">{agentDetails.usedTokens ?? 0}</div>
              </div>
            </div>

            <div className="stat-box bg-green-50 p-4 rounded-lg border border-green-100 flex items-center space-x-3">
              <div className="p-3 bg-green-600 text-white rounded-full">
                <i className="fas fa-wallet text-xl"></i>
              </div>
              <div>
                <div className="text-xs uppercase font-bold text-green-600 tracking-wider">Wallet Balance</div>
                <div className="text-2xl font-extrabold text-green-900 mt-0.5">₹{agentDetails.walletBalance || '0.00'}</div>
              </div>
            </div>

            <div className="stat-box bg-purple-50 p-4 rounded-lg border border-purple-100 flex items-center space-x-3">
              <div className="p-3 bg-purple-600 text-white rounded-full">
                <i className="fas fa-user-shield text-xl"></i>
              </div>
              <div>
                <div className="text-xs uppercase font-bold text-purple-600 tracking-wider">Agent Details</div>
                <div className="text-sm font-bold text-purple-900 mt-0.5">{agentDetails.name || 'N/A'}</div>
                <div className="text-xs text-purple-700 font-mono">ID: {agentDetails.agentId || 'N/A'}</div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-4 items-center justify-between pt-4 border-t border-gray-100">
            <button
              onClick={handleRequestTokens}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold shadow-md flex items-center space-x-2 transition"
            >
              <i className="fas fa-ticket-alt"></i>
              <span>Request Extra Tokens (Support Ticket)</span>
            </button>

            <button
              onClick={() => navigate('/agent/dashboard/tokenhistory')}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-5 py-3 rounded-lg font-medium border border-gray-300 flex items-center space-x-2 transition"
            >
              <i className="fas fa-history"></i>
              <span>View Full Token Audit Ledger</span>
            </button>
          </div>
        </div>
      )}

      {/* How Tokens Work Information Card */}
      <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
          <i className="fas fa-info-circle text-blue-600 mr-2"></i>
          How Token Allocation & Rewards Work
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-lg mb-3">1</div>
            <h3 className="font-bold text-gray-900 text-base mb-1">Default Account Tokens</h3>
            <p className="text-sm text-gray-600">
              When your agent account is approved, default tokens (20 tokens) are automatically credited to your token account to get you started immediately.
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold text-lg mb-3">2</div>
            <h3 className="font-bold text-gray-900 text-base mb-1">Booking Reward Tokens</h3>
            <p className="text-sm text-gray-600">
              For every completed hotel voucher booking, your account is automatically rewarded with bonus tokens upon voucher generation.
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-bold text-lg mb-3">3</div>
            <h3 className="font-bold text-gray-900 text-base mb-1">Request Extra Tokens</h3>
            <p className="text-sm text-gray-600">
              Direct token recharges are disabled. If your tokens are low or exhausted, raise a support ticket of type <b>"Extra Token Request"</b> for Support/Admin review and manual allocation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenRecharge;